import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { IsoDateSchema, MessageSchema, MoneyStringSchema } from './primitives.js';
const c = initContract();
/**
 * FIN-07 — Fixed Assets (`fa`, accounting area per 16.2.2 row 6).
 *
 * Permission mapping (FIN-07 §9 through the 12×5 grid — the grid is closed,
 * BR-PERM-36, BR-PERM-37, so the screen's named gates resolve to grid actions):
 *   read register/schedule/log/KPIs        = accounting.view
 *   register an asset                      = accounting.create
 *   edit identity / own method             = accounting.edit
 *   add or remove a maintenance record     = accounting.edit  (§9: "an
 *     ordinary write permission" — R1: permission-gated, never mode-gated)
 *   Run Depreciation / Dispose (both POST) = accounting.approve (AP-2's
 *     journal-post permission carries the §9 "and" clause)
 *   category defaults                      = admin.edit, service-checked so
 *     the refusal carries BR-FIN-07-18's exact sentence. FIN-07 §2 names
 *     "Administrator, or the Owner" for this row, and admin.edit is the
 *     grid permission with exactly that default reach — a named
 *     micro-permission would break the closed grid (16.4.2's argument).
 */
export const ASSET_CATEGORY_KEYS = [
    'equipment',
    'vehicles',
    'buildings',
    'furniture',
    'it_hardware',
    'other',
];
export const DEPRECIATION_METHODS = [
    'straight_line',
    'declining_balance',
    'units_of_production',
];
/**
 * The stored statuses of §5a field 11 — the spec's four plus 'draft', added by
 * delegated ruling D32 (26 Aug 2026): "code+name alone may exist as a draft;
 * nothing depreciates until all eight fields are present." A draft's chip IS
 * its stored status (there are no figures to derive one from); for the other
 * four the chip may still derive Fully Depreciated ahead of the store
 * (BR-FIN-07-15).
 */
export const ASSET_STATUSES = [
    'draft',
    'in_use',
    'under_maintenance',
    'fully_depreciated',
    'disposed',
];
/**
 * §5b's category table — defaults seeded per tenant, then editable behind
 * the category-configuration gate. Cost accounts per §5f (chart-wins codes).
 */
export const ASSET_CATEGORY_SEED = [
    { key: 'equipment', name: 'Equipment', nameAr: 'معدات', color: '#0ea5e9', costAccountCode: '1510', defaultLifeYears: 10, defaultMethod: 'straight_line' },
    { key: 'vehicles', name: 'Vehicles', nameAr: 'مركبات', color: '#f59e0b', costAccountCode: '1530', defaultLifeYears: 8, defaultMethod: 'declining_balance' },
    { key: 'buildings', name: 'Buildings', nameAr: 'مبانٍ', color: '#8b5cf6', costAccountCode: '1540', defaultLifeYears: 25, defaultMethod: 'straight_line' },
    { key: 'furniture', name: 'Furniture', nameAr: 'أثاث', color: '#10b981', costAccountCode: '1550', defaultLifeYears: 7, defaultMethod: 'straight_line' },
    { key: 'it_hardware', name: 'IT Hardware', nameAr: 'أجهزة تقنية المعلومات', color: '#ef4444', costAccountCode: '1560', defaultLifeYears: 3, defaultMethod: 'declining_balance' },
    { key: 'other', name: 'Other', nameAr: 'أخرى', color: '#64748b', costAccountCode: '1570', defaultLifeYears: 5, defaultMethod: 'straight_line' },
];
// ── Fixed sentences ─────────────────────────────────────────────────────────
export const FA_MESSAGES = {
    /** BR-FIN-07-18, verbatim. */
    categoryConfigDenied: 'You do not have permission to change depreciation settings. Ask an administrator to grant it.',
    /**
     * BR-FIN-07-13 / the §13 banner — and ERR-FIN-07-05, where the prototype's
     * banner SAID read-only while the fields still accepted typing. This is the
     * server refusal under it, so the banner is now true.
     */
    disposedReadOnly: 'This asset is disposed and read-only. It remains on record with full history.',
    /**
     * ERR-FIN-07-06 — the status-dropdown defect, fixed: setting the status to
     * Disposed left the asset disposed with no disposal record and no journal
     * entry. The dialogue is now the only route.
     */
    disposeViaDialogue: 'An asset is disposed through Dispose asset, which records the disposal and posts the entry — never by setting the status.',
    /** §15: salvage above cost "must be refused outright". */
    salvageAboveCost: 'The salvage value cannot exceed the cost.',
    negativeMoney: 'Costs and salvage values cannot be negative.',
    /** ERR-FIN-07-01 / BR-FIN-07-09. */
    nothingEligible: 'No asset is eligible for this month. Nothing was posted.',
    /** Q-FIN-07-3. */
    monthAlreadyRun: (month) => `Depreciation for ${month} has already been posted for every eligible asset.`,
    alreadyDisposed: 'This asset is already disposed.',
    /** §6: proceeds only when sold. */
    proceedsOnlySold: 'Scrapping or donating an asset takes no proceeds.',
    /** §15 Deleted linked record (FK-backed). */
    categoryInUse: 'A category with assets cannot be removed.',
    maintenanceRemoved: 'This maintenance entry is already removed.',
    /** §15 Zero: a blank asset never depreciates. */
    zeroCostNeverEligible: 'An asset with no cost has no schedule and is never eligible for a run.',
    /**
     * BR-FIN-07-01 under D32 — the draft refusal, NAMING the missing fields.
     * `fields` is the pre-joined list in BR-FIN-07-01's own order (category,
     * purchase date, cost, useful life, salvage value, method), composed by
     * `missingDraftFields` so the run dialogue and the leave-draft refusal
     * cannot name different sets. Parameterised, so it is on the D46 queue in
     * `refusal-census.spec.ts` rather than the exact-match map.
     */
    draftMissingFields: (fields) => `This asset is a draft — still missing: ${fields}. Nothing depreciates until all eight fields are present.`,
    /** A draft never entered the books, so §12's disposal entry has nothing to remove. */
    draftNotDisposable: 'A draft asset cannot be disposed — it has no cost on the books to remove. Complete its fields first.',
};
/**
 * D46 — FIN-07's two parameterised depreciation refusals, structured.
 * `monthAlreadyRun` carries the month; `draftMissingFields` the pre-joined list
 * of missing fields (composed by `missingDraftFields`, BR-FIN-07-01's order).
 * Both members ALSO ride the run report as exclusion reasons — those sites keep
 * the `FA_MESSAGES` twin; only the HTTP raise sites throw the builder. No token,
 * no plural. Sentence parity pinned by `refusal-census.spec.ts`.
 */
export const FA_REFUSALS = {
    monthAlreadyRun: (month) => ({
        message: FA_MESSAGES.monthAlreadyRun(month),
        key: 'fa.errMonthAlreadyRun',
        params: { month },
    }),
    draftMissingFields: (fields) => ({
        message: FA_MESSAGES.draftMissingFields(fields),
        key: 'fa.errDraftMissingFields',
        params: { fields },
    }),
};
// ── Schemas ─────────────────────────────────────────────────────────────────
export const AssetCategorySchema = z
    .object({
    id: z.uuid(),
    key: z.enum(ASSET_CATEGORY_KEYS),
    name: z.string(),
    nameAr: z.string().nullable(),
    color: z.string(),
    costAccountCode: z.string(),
    defaultLifeYears: z.number().int().positive(),
    defaultMethod: z.enum(DEPRECIATION_METHODS),
    assetCount: z.number().int(),
})
    .strict();
export const UpdateAssetCategoryRequestSchema = z
    .object({
    defaultLifeYears: z.number().int().min(1).max(100).optional(),
    defaultMethod: z.enum(DEPRECIATION_METHODS).optional(),
})
    .strict();
const SignedMoneySchema = z.string().regex(/^-?\d{1,13}(\.\d{1,2})?$/);
/** One schedule row (§5c): a year, its slice, and where the value stands. */
export const ScheduleRowSchema = z
    .object({
    year: z.number().int(),
    charge: MoneyStringSchema,
    accumulated: MoneyStringSchema,
    netBookValue: MoneyStringSchema,
    /** The amber badge: "Prorated · 63%" on the first row, else null. */
    proratedPct: z.number().int().nullable(),
})
    .strict();
export const AssetSummarySchema = z
    .object({
    id: z.uuid(),
    /** FA-#### (§5a field 2). */
    code: z.string(),
    name: z.string(),
    /**
     * D32: null on a draft that has no category yet — the register draws a
     * neutral square and no category name, never an invented Equipment.
     */
    categoryKey: z.enum(ASSET_CATEGORY_KEYS).nullable(),
    categoryName: z.string().nullable(),
    categoryColor: z.string().nullable(),
    status: z.enum(ASSET_STATUSES),
    /** BR-FIN-07-15: Disposed → Fully Depreciated → stored status. */
    chip: z.enum(ASSET_STATUSES),
    /** D32: null while a draft has no cost — absent, not zero. */
    cost: MoneyStringSchema.nullable(),
    netBookValue: MoneyStringSchema.nullable(),
})
    .strict();
export const MaintenanceEntrySchema = z
    .object({
    id: z.uuid(),
    serviceDate: IsoDateSchema,
    cost: MoneyStringSchema,
    description: z.string().nullable(),
    nextService: IsoDateSchema.nullable(),
    createdAt: z.string(),
    /** BR-FIN-07-19: a removal is recorded, and the record stays visible. */
    removedAt: z.string().nullable(),
})
    .strict();
export const AssetDisposalSchema = z
    .object({
    id: z.uuid(),
    disposalDate: IsoDateSchema,
    method: z.enum(['sold', 'scrapped', 'donated']),
    proceeds: MoneyStringSchema,
    nbvAtDisposal: MoneyStringSchema,
    /** Signed: + gain, − loss (BR-FIN-07-11). */
    gainLoss: SignedMoneySchema,
    journalEntryId: z.uuid(),
    postedAt: z.string(),
})
    .strict();
export const AssetSchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    /**
     * D32: the six of BR-FIN-07-01's eight fields a draft may still lack are
     * nullable here — null is "not yet given", drawn as an empty box, never a
     * default nobody typed. A non-draft asset carries all six (the
     * `assets_draft_completeness` CHECK is the floor).
     */
    categoryId: z.uuid().nullable(),
    categoryKey: z.enum(ASSET_CATEGORY_KEYS).nullable(),
    categoryName: z.string().nullable(),
    categoryColor: z.string().nullable(),
    purchaseDate: IsoDateSchema.nullable(),
    cost: MoneyStringSchema.nullable(),
    supplier: z.string().nullable(),
    location: z.string().nullable(),
    lifeValue: z.number().int().positive().nullable(),
    lifeUnit: z.enum(['years', 'months']),
    salvage: MoneyStringSchema.nullable(),
    method: z.enum(DEPRECIATION_METHODS).nullable(),
    status: z.enum(ASSET_STATUSES),
    chip: z.enum(ASSET_STATUSES),
    /** §5d's worked-out values, as of today (or the disposal date). A draft
     *  has accumulated nothing and its value is unknowable, not zero. */
    accumulated: MoneyStringSchema,
    netBookValue: MoneyStringSchema.nullable(),
    /** §5d row 3 — relabelled "of depreciable amount" (the defect's fix). */
    pctDepreciated: z.number().int(),
    schedule: ScheduleRowSchema.array(),
    maintenance: MaintenanceEntrySchema.array(),
    maintenanceTotal: MoneyStringSchema,
    disposal: AssetDisposalSchema.nullable(),
})
    .strict();
export const CreateAssetRequestSchema = z
    .object({
    /**
     * BR-FIN-07-01 under D32: every field stays optional on the wire, but
     * omission now means ABSENT, not silently defaulted — a request missing
     * any of the eight yields a DRAFT holding exactly what was typed (name
     * omitted → "Untitled asset", the one §6 blank-register courtesy that
     * survives, since even a draft needs its name half of "code+name").
     * Category defaults for life and method still apply when a category is
     * chosen (§5b: "applied when an asset is registered").
     */
    name: z.string().trim().min(1).max(200).optional(),
    categoryKey: z.enum(ASSET_CATEGORY_KEYS).optional(),
    purchaseDate: IsoDateSchema.optional(),
    cost: MoneyStringSchema.optional(),
    supplier: z.string().trim().max(200).optional(),
    location: z.string().trim().max(200).optional(),
    lifeValue: z.number().int().min(1).max(1200).optional(),
    lifeUnit: z.enum(['years', 'months']).optional(),
    salvage: MoneyStringSchema.optional(),
    method: z.enum(DEPRECIATION_METHODS).optional(),
})
    .strict();
export const UpdateAssetRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    /** BR-FIN-07-06: changing this re-applies the category's defaults. */
    categoryKey: z.enum(ASSET_CATEGORY_KEYS).optional(),
    purchaseDate: IsoDateSchema.optional(),
    cost: MoneyStringSchema.optional(),
    supplier: z.string().trim().max(200).nullable().optional(),
    location: z.string().trim().max(200).nullable().optional(),
    lifeValue: z.number().int().min(1).max(1200).optional(),
    lifeUnit: z.enum(['years', 'months']).optional(),
    salvage: MoneyStringSchema.optional(),
    method: z.enum(DEPRECIATION_METHODS).optional(),
    /**
     * 'disposed' is NOT here — the dialogue is the only route (the defect
     * fixed). 'draft' is not here either: a draft leaves draft by being given
     * one of these three (refused, naming the missing fields, until all eight
     * are present — D32), and a live asset can never be demoted back.
     */
    status: z.enum(['in_use', 'under_maintenance', 'fully_depreciated']).optional(),
})
    .strict();
// ── Depreciation runs (§6, §12A) ────────────────────────────────────────────
const MonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'A month is YYYY-MM');
export const RunPreviewSchema = z
    .object({
    month: MonthSchema,
    /** Every eligible asset with its monthly charge (§6's dialogue list). */
    lines: z
        .object({
        assetId: z.uuid(),
        code: z.string(),
        name: z.string(),
        amount: MoneyStringSchema,
    })
        .strict()
        .array(),
    total: MoneyStringSchema,
    /** Why the absent are absent (§13: printer at salvage, sedan disposed…). */
    excluded: z
        .object({ assetId: z.uuid(), code: z.string(), name: z.string(), reason: z.string() })
        .strict()
        .array(),
})
    .strict();
export const DepreciationRunSchema = z
    .object({
    id: z.uuid(),
    /** DR-####. */
    code: z.string(),
    month: MonthSchema,
    total: MoneyStringSchema,
    journalEntryId: z.uuid(),
    postedAt: z.string(),
    lines: z
        .object({ assetId: z.uuid(), code: z.string(), name: z.string(), amount: MoneyStringSchema })
        .strict()
        .array(),
})
    .strict();
// ── Disposal (§6, §12B/C/D) ─────────────────────────────────────────────────
export const DisposeAssetRequestSchema = z
    .object({
    disposalDate: IsoDateSchema,
    method: z.enum(['sold', 'scrapped', 'donated']),
    /** Only when sold (§6); refused otherwise. */
    proceeds: MoneyStringSchema.optional(),
})
    .strict();
/** The live §6 preview: NBV at the date, the result tile, the entry lines. */
export const DisposalPreviewSchema = z
    .object({
    disposalDate: IsoDateSchema,
    nbvAtDisposal: MoneyStringSchema,
    accumulatedAtDisposal: MoneyStringSchema,
    proceeds: MoneyStringSchema,
    gainLoss: SignedMoneySchema,
    /** §12's five conditional lines, in the order they are written. */
    entryLines: z
        .object({
        accountCode: z.string(),
        accountName: z.string(),
        debit: MoneyStringSchema.nullable(),
        credit: MoneyStringSchema.nullable(),
    })
        .strict()
        .array(),
})
    .strict();
export const AddMaintenanceRequestSchema = z
    .object({
    /** §5e: only the date is required. */
    serviceDate: IsoDateSchema,
    cost: MoneyStringSchema.optional(),
    description: z.string().trim().max(500).optional(),
    nextService: IsoDateSchema.optional(),
})
    .strict();
export const AssetKpisSchema = z
    .object({
    totalCost: MoneyStringSchema,
    accumulated: MoneyStringSchema,
    netBookValue: MoneyStringSchema,
    /** Not disposed (the §4 strip's "Active" tile). */
    activeCount: z.number().int(),
})
    .strict();
// ── Router ──────────────────────────────────────────────────────────────────
const assetParams = z.object({ assetId: z.uuid() }).strict();
export const fixedAssetsContract = c.router({
    listAssets: {
        method: 'GET',
        path: '/api/assets',
        query: z
            .object({
            category: z.enum(ASSET_CATEGORY_KEYS).optional(),
            /** Filters on the DERIVED chip (BR-FIN-07-16, the filter defect fixed). */
            status: z.enum(ASSET_STATUSES).optional(),
            search: z.string().max(200).optional(),
        })
            .strict(),
        responses: {
            200: z.object({ kpis: AssetKpisSchema, assets: AssetSummarySchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The register + KPI strip; search matches name, tag or category (accounting.view).',
    },
    getAsset: {
        method: 'GET',
        path: '/api/assets/:assetId',
        pathParams: assetParams,
        responses: {
            200: AssetSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One asset: identity, tiles, schedule, maintenance log, disposal (accounting.view).',
    },
    createAsset: {
        method: 'POST',
        path: '/api/assets',
        body: CreateAssetRequestSchema,
        responses: {
            201: AssetSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Register an asset — blank = "Untitled asset" with category defaults (§6) (accounting.create).',
    },
    updateAsset: {
        method: 'PATCH',
        path: '/api/assets/:assetId',
        pathParams: assetParams,
        body: UpdateAssetRequestSchema,
        responses: {
            200: AssetSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit an asset; disposed is read-only (BR-FIN-07-13); category change re-applies defaults (BR-FIN-07-06) (accounting.edit).',
    },
    listCategories: {
        method: 'GET',
        path: '/api/assets/categories',
        responses: {
            200: z.object({ categories: AssetCategorySchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The six categories with their defaults (§5b) (accounting.view).',
    },
    updateCategory: {
        method: 'PATCH',
        path: '/api/assets/categories/:categoryId',
        pathParams: z.object({ categoryId: z.uuid() }).strict(),
        body: UpdateAssetCategoryRequestSchema,
        responses: {
            200: AssetCategorySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Category defaults — service-checks admin.edit and refuses with BR-FIN-07-18\'s sentence.',
    },
    previewDepreciation: {
        method: 'POST',
        path: '/api/assets/depreciation/preview',
        body: z.object({ month: MonthSchema }).strict(),
        responses: {
            200: RunPreviewSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The Run Depreciation dialogue: eligible assets + charges + the consolidated preview (accounting.view).',
    },
    postDepreciation: {
        method: 'POST',
        path: '/api/assets/depreciation/runs',
        body: z.object({ month: MonthSchema }).strict(),
        responses: {
            201: DepreciationRunSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Post the batch: one 5140 debit, one 1520 credit per asset (BR-FIN-07-08); nothing eligible posts nothing (BR-FIN-07-09) (accounting.approve).',
    },
    listDepreciationRuns: {
        method: 'GET',
        path: '/api/assets/depreciation/runs',
        responses: {
            200: z.object({ runs: DepreciationRunSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Posted runs, newest first (accounting.view).',
    },
    previewDisposal: {
        method: 'GET',
        path: '/api/assets/:assetId/disposal-preview',
        pathParams: assetParams,
        query: z
            .object({
            date: IsoDateSchema,
            method: z.enum(['sold', 'scrapped', 'donated']),
            proceeds: MoneyStringSchema.optional(),
        })
            .strict(),
        responses: {
            200: DisposalPreviewSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'The live §6 preview — NBV at the date, gain/loss tile, entry lines (accounting.view).',
    },
    disposeAsset: {
        method: 'POST',
        path: '/api/assets/:assetId/dispose',
        pathParams: assetParams,
        body: DisposeAssetRequestSchema,
        responses: {
            201: AssetSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Dispose & post entry — measured at the disposal date (BR-FIN-07-10, BR-FIN-07-11, BR-FIN-07-12); final (Q-FIN-07-4) (accounting.approve).',
    },
    addMaintenance: {
        method: 'POST',
        path: '/api/assets/:assetId/maintenance',
        pathParams: assetParams,
        body: AddMaintenanceRequestSchema,
        responses: {
            201: MaintenanceEntrySchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Add a maintenance record — posts nothing, changes no figure (BR-FIN-07-14, BR-FIN-07-17) (accounting.edit).',
    },
    removeMaintenance: {
        method: 'DELETE',
        path: '/api/assets/:assetId/maintenance/:entryId',
        pathParams: z.object({ assetId: z.uuid(), entryId: z.uuid() }).strict(),
        body: z.object({}).strict().optional(),
        responses: {
            200: MaintenanceEntrySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Record a removal — who and when; the entry stays on record (BR-FIN-07-19) (accounting.edit).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=fixed-assets.js.map